import {
  fileOpen as _fileOpen,
  fileSave as _fileSave,
  supported as nativeFileSystemSupported,
} from "browser-fs-access";

import { EVENT, MIME_TYPES, debounce } from "@excalidraw/common";

import { AbortError } from "../errors";

import { normalizeFile } from "./blob";

import type { FileSystemHandle } from "browser-fs-access";

type FILE_EXTENSION = Exclude<keyof typeof MIME_TYPES, "binary">;

const INPUT_CHANGE_INTERVAL_MS = 5000;

export const fileOpen = async <M extends boolean | undefined = false>(opts: {
  extensions?: FILE_EXTENSION[];
  description: string;
  multiple?: M;
}): Promise<M extends false | undefined ? File : File[]> => {
  // an unsafe TS hack, alas not much we can do AFAIK
  type RetType = M extends false | undefined ? File : File[];

  const mimeTypes = opts.extensions?.reduce((mimeTypes, type) => {
    mimeTypes.push(MIME_TYPES[type]);

    return mimeTypes;
  }, [] as string[]);

  const extensions = opts.extensions?.reduce((acc, ext) => {
    if (ext === "jpg") {
      return acc.concat(".jpg", ".jpeg");
    }
    return acc.concat(`.${ext}`);
  }, [] as string[]);

  const files = await _fileOpen({
    description: opts.description,
    extensions,
    mimeTypes,
    multiple: opts.multiple ?? false,
    legacySetup: (resolve, reject, input) => {
      // allow picking the same file repeatedly (some browsers won't fire
      // change if the same file is selected again)
      input.value = "";

      // Ensure the legacy <input type="file"> doesn't cause the viewport to
      // scroll when it receives focus (some browsers try to scroll focused
      // inputs into view). This keeps top UI (toolbar/navbar) from "jumping"
      // after selecting files (e.g. image upload).
      input.style.position = "fixed";
      input.style.top = "0";
      input.style.left = "0";
      input.style.width = "0";
      input.style.height = "0";
      input.style.opacity = "0";
      input.style.border = "0";
      input.style.padding = "0";
      input.style.margin = "0";
      input.style.pointerEvents = "none";
      input.style.zIndex = "-1";

      const rejectWithAbortError = () => {
        (reject as unknown as (reason?: unknown) => void)(new AbortError());
      };
      const scheduleRejection = debounce(
        rejectWithAbortError,
        INPUT_CHANGE_INTERVAL_MS,
      );
      const focusHandler = () => {
        checkForFile();
        document.addEventListener(EVENT.KEYUP, scheduleRejection);
        document.addEventListener(EVENT.POINTER_UP, scheduleRejection);

        // When the user cancels the picker, some browsers only restore focus
        // without emitting an input change event. The previous behavior would
        // keep the promise pending for up to INPUT_CHANGE_INTERVAL_MS.
        // Instead, do a short retry loop and abort quickly if no file chosen.
        const startedAt = Date.now();
        const fastInterval = window.setInterval(() => {
          checkForFile();
          if (input.files?.length) {
            clearInterval(fastInterval);
            return;
          }
          if (Date.now() - startedAt > 300) {
            clearInterval(fastInterval);
            rejectWithAbortError();
          }
        }, 50);

        scheduleRejection();
      };
      const checkForFile = () => {
        // this hack might not work when expecting multiple files
        if (input.files?.length) {
          const ret = opts.multiple ? [...input.files] : input.files[0];
          resolve(ret as RetType);
        }
      };
      requestAnimationFrame(() => {
        window.addEventListener(EVENT.FOCUS, focusHandler);
      });
      const interval = window.setInterval(() => {
        checkForFile();
      }, INPUT_CHANGE_INTERVAL_MS);
      return (rejectPromise) => {
        clearInterval(interval);
        scheduleRejection.cancel();
        window.removeEventListener(EVENT.FOCUS, focusHandler);
        document.removeEventListener(EVENT.KEYUP, scheduleRejection);
        document.removeEventListener(EVENT.POINTER_UP, scheduleRejection);
        if (rejectPromise) {
          // so that something is shown in console if we need to debug this
          console.warn("Opening the file was canceled (legacy-fs).");
          rejectPromise(new AbortError());
        }
      };
    },
  });

  if (Array.isArray(files)) {
    return (await Promise.all(
      files.map((file) => normalizeFile(file)),
    )) as RetType;
  }
  return (await normalizeFile(files)) as RetType;
};

export const fileSave = (
  blob: Blob | Promise<Blob>,
  opts: {
    /** supply without the extension */
    name: string;
    /** file extension */
    extension: FILE_EXTENSION;
    mimeTypes?: string[];
    description: string;
    /** existing FileSystemHandle */
    fileHandle?: FileSystemHandle | null;
  },
) => {
  return _fileSave(
    blob,
    {
      fileName: `${opts.name}.${opts.extension}`,
      description: opts.description,
      extensions: [`.${opts.extension}`],
      mimeTypes: opts.mimeTypes,
    },
    opts.fileHandle,
  );
};

export { nativeFileSystemSupported };
export type { FileSystemHandle };
